import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AppConfigService } from '~/core/services/appconfig.service';

// Dashboard response interfaces
export interface MetricsSummary {
  total_active_users: number;
  total_questions_asked: number;
  questions_with_feedback: number;
  total_sessions: number;
}

export interface FeedbackSentiment {
  likes: number;
  dislikes: number;
  neutral: number;
}

export interface DailyActiveUser {
  date: string;
  user_count: number;
}

export interface MetricsDashboardResponse {
  status: string;
  data: {
    summary: MetricsSummary;
    feedback_sentiment: FeedbackSentiment;
    daily_active_users: DailyActiveUser[];
  };
  metadata: {
    date_range: {
      start_date: string;
      end_date: string;
    };
    cached: boolean;
    cache_expires_at: string;
  };
  retrieved_at: string;
}

// Active Users response interfaces
export interface ActiveUser {
  user_id: string;
  name: string;
  email: string;
}

export interface ActiveUsersResponse {
  status: string;
  data: ActiveUser[];
  metadata: {
    date_range: {
      start_date: string;
      end_date: string;
    };
    cached: boolean;
    cache_expires_at: string | null;
  };
  retrieved_at: string;
}

// Questions response interfaces
export interface Question {
  message_id: string;
  session_id: string;
  user_id: string;
  name: string;
  email: string;
  content: string;
  timestamp: string;
}

export interface QuestionsResponse {
  status: string;
  data: Question[];
  metadata: {
    date_range: {
      start_date: string;
      end_date: string;
    };
    cached: boolean;
    cache_expires_at: string | null;
  };
  pagination: {
    page: number;
    page_size: number;
    total_count: number;
    total_pages: number;
  };
  retrieved_at: string;
}

// Feedback response interfaces
export interface FeedbackComment {
  message_id: string;
  session_id: string;
  user_id: string;
  name: string;
  email: string;
  question: string;
  answer: string;
  sentiment: 'Like' | 'Dislike';
  issue_type: string;
  response_rating: number;
  additional_comments: string;
  timestamp: string;
}

export interface FeedbackResponse {
  status: string;
  data: FeedbackComment[];
  metadata: {
    date_range: {
      start_date: string;
      end_date: string;
    };
    cached: boolean;
    cache_expires_at: string | null;
  };
  pagination: {
    page: number;
    page_size: number;
    total_count: number;
    total_pages: number;
  };
  retrieved_at: string;
}

@Injectable({
  providedIn: 'root'
})
export class MetricsApiService {
  private readonly ENDPOINT = 'api/v1/user_metrics/dashboard';
  private readonly ACTIVE_USERS_ENDPOINT = 'api/v1/user_metrics/active_users';
  private readonly QUESTIONS_ENDPOINT = 'api/v1/user_metrics/questions';
  private readonly FEEDBACK_ENDPOINT = 'api/v1/user_metrics/feedback';
  private readonly DEFAULT_PAGE_SIZE = 15;

  constructor(
    private http: HttpClient,
    private appConfigService: AppConfigService
  ) {}

  /**
   * Get metrics dashboard data for a date range
   */
  getMetricsDashboard(
    startDate: string,
    endDate: string,
    bypassCache: boolean = false
  ): Observable<MetricsDashboardResponse> {
    const payload = {
      start_date: startDate,
      end_date: endDate,
      bypass_cache: bypassCache
    };

    const baseUrl = this.appConfigService.getApiUrl();
    
    if (!baseUrl) {
      throw new Error('API URL is not configured');
    }
    
    // Use the utility to properly concatenate URLs
    const apiUrl = this.appConfigService.concatenateUrl(baseUrl, this.ENDPOINT);
    
    
    return this.http.post<MetricsDashboardResponse>(apiUrl, payload);
  }

  /**
   * Get active users with pagination
   * @param startDate - Start date (YYYY-MM-DD)
   * @param endDate - End date (YYYY-MM-DD)
   * @param page - Page number (1-indexed)
   * @param pageSize - Items per page (default: 15)
   * @param bypassCache - Whether to bypass cache
   */
  getActiveUsers(
    startDate: string,
    endDate: string,
    page: number = 1,
    pageSize: number = this.DEFAULT_PAGE_SIZE,
    bypassCache: boolean = false
  ): Observable<ActiveUsersResponse> {
    const payload = {
      start_date: startDate,
      end_date: endDate,
      page: page,
      page_size: pageSize,
      bypass_cache: bypassCache
    };

    const baseUrl = this.appConfigService.getApiUrl();
    if (!baseUrl) {
      throw new Error('API URL is not configured');
    }

    const apiUrl = this.appConfigService.concatenateUrl(baseUrl, this.ACTIVE_USERS_ENDPOINT);

    return this.http.post<ActiveUsersResponse>(apiUrl, payload);
  }

  /**
   * Get all active users by iterating through all pages
   * @param startDate - Start date (YYYY-MM-DD)
   * @param endDate - End date (YYYY-MM-DD)
   * @param bypassCache - Whether to bypass cache
   */
  getAllActiveUsers(
    startDate: string,
    endDate: string,
    bypassCache: boolean = false
  ): Observable<ActiveUser[]> {
    return new Observable(observer => {
      const allUsers: ActiveUser[] = [];
      let currentPage = 1;

      const fetchPage = () => {
        this.getActiveUsers(startDate, endDate, currentPage, this.DEFAULT_PAGE_SIZE, bypassCache)
          .subscribe({
            next: (response: ActiveUsersResponse) => {
              const users = response.data || [];
              allUsers.push(...users);


              // Note: Active users API doesn't have pagination info, so we assume single page
              observer.next(allUsers);
              observer.complete();
            },
            error: (err) => observer.error(err)
          });
      };

      fetchPage();
    });
  }

  /**
   * Get questions asked with pagination and optional filters
   * @param startDate - Start date (YYYY-MM-DD)
   * @param endDate - End date (YYYY-MM-DD)
   * @param page - Page number (1-indexed)
   * @param pageSize - Items per page (default: 15)
   * @param bypassCache - Whether to bypass cache
   * @param userIds - Optional array of user IDs to filter by
   */
  getQuestions(
    startDate: string,
    endDate: string,
    page: number = 1,
    pageSize: number = this.DEFAULT_PAGE_SIZE,
    bypassCache: boolean = false,
    userIds?: string[]
  ): Observable<QuestionsResponse> {
    const payload: any = {
      start_date: startDate,
      end_date: endDate,
      page: page,
      page_size: pageSize,
      bypass_cache: bypassCache
    };

    // Add user_ids filter if provided
    if (userIds && userIds.length > 0) {
      payload.user_ids = userIds;
    }

    const baseUrl = this.appConfigService.getApiUrl();
    if (!baseUrl) {
      throw new Error('API URL is not configured');
    }

    const apiUrl = this.appConfigService.concatenateUrl(baseUrl, this.QUESTIONS_ENDPOINT);

    return this.http.post<QuestionsResponse>(apiUrl, payload);
  }

  /**
   * Get all questions by iterating through all pages
   * @param startDate - Start date (YYYY-MM-DD)
   * @param endDate - End date (YYYY-MM-DD)
   * @param bypassCache - Whether to bypass cache
   */
  getAllQuestions(
    startDate: string,
    endDate: string,
    bypassCache: boolean = false
  ): Observable<Question[]> {
    return new Observable(observer => {
      const allQuestions: Question[] = [];
      let currentPage = 1;

      const fetchPage = () => {
        this.getQuestions(startDate, endDate, currentPage, this.DEFAULT_PAGE_SIZE, bypassCache)
          .subscribe({
            next: (response: QuestionsResponse) => {
              const questions = response.data || [];
              allQuestions.push(...questions);

              // Check if there are more pages
              const totalPages = response.pagination.total_pages;


              if (currentPage < totalPages) {
                currentPage++;
                fetchPage();
              } else {
                observer.next(allQuestions);
                observer.complete();
              }
            },
            error: (err) => observer.error(err)
          });
      };

      fetchPage();
    });
  }

  /**
   * Get feedback with pagination and optional filters
   * @param startDate - Start date (YYYY-MM-DD)
   * @param endDate - End date (YYYY-MM-DD)
   * @param page - Page number (1-indexed)
   * @param pageSize - Items per page (default: 15)
   * @param bypassCache - Whether to bypass cache
   * @param userIds - Optional array of user IDs to filter by
   * @param ratings - Optional array of rating numbers (1-5) to filter by
   */
  getFeedback(
    startDate: string,
    endDate: string,
    page: number = 1,
    pageSize: number = this.DEFAULT_PAGE_SIZE,
    bypassCache: boolean = false,
    userIds?: string[],
    ratings?: number[]
  ): Observable<FeedbackResponse> {
    const payload: any = {
      start_date: startDate,
      end_date: endDate,
      page: page,
      page_size: pageSize,
      bypass_cache: bypassCache
    };

    // Add user_ids filter if provided
    if (userIds && userIds.length > 0) {
      payload.user_ids = userIds;
    }

    // Add ratings filter if provided
    if (ratings && ratings.length > 0) {
      payload.ratings = ratings;
    }

    const baseUrl = this.appConfigService.getApiUrl();
    if (!baseUrl) {
      throw new Error('API URL is not configured');
    }

    const apiUrl = this.appConfigService.concatenateUrl(baseUrl, this.FEEDBACK_ENDPOINT);

    return this.http.post<FeedbackResponse>(apiUrl, payload);
  }

  /**
   * Get all feedback by iterating through all pages
   * @param startDate - Start date (YYYY-MM-DD)
   * @param endDate - End date (YYYY-MM-DD)
   * @param bypassCache - Whether to bypass cache
   */
  getAllFeedback(
    startDate: string,
    endDate: string,
    bypassCache: boolean = false
  ): Observable<FeedbackComment[]> {
    return new Observable(observer => {
      const allFeedback: FeedbackComment[] = [];
      let currentPage = 1;

      const fetchPage = () => {
        this.getFeedback(startDate, endDate, currentPage, this.DEFAULT_PAGE_SIZE, bypassCache)
          .subscribe({
            next: (response: FeedbackResponse) => {
              const feedback = response.data || [];
              allFeedback.push(...feedback);

              // Check if there are more pages
              const totalPages = response.pagination.total_pages;


              if (currentPage < totalPages) {
                currentPage++;
                fetchPage();
              } else {
                observer.next(allFeedback);
                observer.complete();
              }
            },
            error: (err) => observer.error(err)
          });
      };

      fetchPage();
    });
  }
}
